import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle, ArrowRight, CheckCircle2, X, User as UserIcon, Clock, Info, Bell } from "lucide-react";
import { getNotifMeta, toneClasses, humanTime, routeForNotif } from "@/lib/notifStyles";
import { useNotifSuppress } from "@/context/NotifSuppressContext";

/**
 * Modal bloqueante — se muestra al iniciar sesión si hay notificaciones críticas
 * sin leer. Rediseño Feb 2026:
 *  - Barra de progreso visual "2 de 5"
 *  - Cada notificación se muestra con su icono/color específico (stock, precio, etc.)
 *  - Meta info (autor, fecha, motivo) se agrupa en un panel discreto abajo
 *  - Botón secundario "Ver detalle" navega a la entidad (pedido/producto)
 *  - Copy más humano: "Necesitamos que confirmes..."
 *  - Se suprime automáticamente durante creación de pedido/cliente (via NotifSuppressContext)
 *    y en su lugar aparece un mini-toast discreto en la esquina.
 *  - Fix: al confirmar todos, la vista de éxito se auto-cierra completamente (antes se quedaba pegada).
 */
export default function CriticalNotificationsGate() {
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();
  const { suppressed } = useNotifSuppress();

  // Jun/2026: avisos pospuestos en esta sesión (no vuelven a bloquear hasta el
  // próximo login). Antes, cualquier mutación re-abría el modal y el usuario
  // quedaba "trancado" mientras otro compañero subía pedidos a su nombre.
  const POSPUESTOS_KEY = "beem_criticas_pospuestas";
  const leerPospuestos = () => {
    try { return new Set(JSON.parse(sessionStorage.getItem(POSPUESTOS_KEY) || "[]")); } catch { return new Set(); }
  };
  const load = async () => {
    try {
      const { data } = await api.get("/notificaciones/criticas");
      const pospuestos = leerPospuestos();
      setItems((data || []).filter((n) => !pospuestos.has(n.id)));
      setIdx(0);
      setDone(false);
    } catch {}
  };
  useEffect(() => {
    load();
    // Refresh cuando llegan notifs nuevas (login, mutaciones, foco de ventana)
    const onRefresh = () => load();
    window.addEventListener("notif-refresh", onRefresh);
    window.addEventListener("focus", onRefresh);
    return () => {
      window.removeEventListener("notif-refresh", onRefresh);
      window.removeEventListener("focus", onRefresh);
    };
  }, []);

  // Nada que mostrar
  if (!items.length && !done) return null;

  const current = items[idx];
  const total = items.length;

  const confirm = async (goDetail = false) => {
    if (!current) return;
    setProcessing(true);
    try {
      await api.post(`/notificaciones/${current.id}/leer`);
      const target = goDetail ? routeForNotif(current) : null;
      if (idx + 1 >= items.length) {
        // Ultima: mostramos vista de exito breve y limpiamos TODO (bug fix)
        setDone(true);
        setTimeout(() => {
          setItems([]);
          setDone(false);
        }, goDetail ? 100 : 1400);
      } else {
        setIdx(idx + 1);
      }
      if (target) navigate(target);
    } finally { setProcessing(false); }
  };

  const cerrarTemp = () => {
    // Posponer: el usuario podrá volver a verlas desde la campanita o
    // /notificaciones. Al próximo login vuelven a aparecer si siguen sin leer.
    try {
      const pospuestos = leerPospuestos();
      items.forEach((n) => pospuestos.add(n.id));
      sessionStorage.setItem(POSPUESTOS_KEY, JSON.stringify([...pospuestos]));
    } catch {}
    setItems([]);
    setDone(false);
  };

  // Vista de éxito al terminar
  if (done) {
    return (
      <Dialog open modal onOpenChange={() => {}}>
        <DialogContent className="max-w-md p-0 gap-0 overflow-hidden [&>button.absolute]:hidden" onEscapeKeyDown={(e) => e.preventDefault()} onInteractOutside={(e) => e.preventDefault()}>
          <div className="p-8 text-center bg-gradient-to-b from-emerald-50 to-transparent dark:from-emerald-500/10">
            <div className="w-16 h-16 mx-auto rounded-full bg-emerald-500/15 flex items-center justify-center mb-4">
              <CheckCircle2 className="w-9 h-9 text-emerald-600" />
            </div>
            <h2 className="font-display text-2xl text-foreground">Todo confirmado</h2>
            <p className="text-sm text-muted-foreground mt-2">Ya puedes continuar trabajando con normalidad.</p>
            <Button onClick={cerrarTemp} className="mt-6 bg-primary hover:bg-primary/90 h-11 px-6" data-testid="critical-done-close">
              Continuar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!current) return null;
  const meta = getNotifMeta(current.tipo);
  const tone = toneClasses(meta.tone, true);
  const Icon = meta.icon;
  const target = routeForNotif(current);
  const progressPct = total > 0 ? ((idx + 1) / total) * 100 : 0;

  // Cuando esta suprimido (creando pedido/cliente) → mostrar mini-toast en la esquina
  // en vez del modal bloqueante. El toast NO interrumpe pero recuerda al usuario.
  if (suppressed) {
    return (
      <div
        className="fixed bottom-24 sm:bottom-6 right-4 sm:right-6 z-40 w-[300px] max-w-[calc(100vw-2rem)] bg-card border-2 border-rose-500/40 rounded-xl shadow-lg overflow-hidden animate-in slide-in-from-bottom-3 fade-in duration-300"
        data-testid="critical-notif-toast"
        role="alert"
      >
        <div className="px-3 py-2 bg-rose-500/10 border-b border-rose-500/20 flex items-center gap-2">
          <AlertTriangle className="w-3.5 h-3.5 text-rose-600 shrink-0" />
          <span className="text-[10px] font-bold uppercase tracking-wider text-rose-700 dark:text-rose-400">
            {total === 1 ? "1 aviso urgente" : `${total} avisos urgentes`}
          </span>
          <span className="ml-auto text-[10px] text-muted-foreground">Pendiente</span>
        </div>
        <div className="p-3">
          <div className="flex items-start gap-2">
            <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${tone.iconBg}`}>
              <Icon className={`w-3.5 h-3.5 ${tone.iconFg}`} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[12.5px] font-semibold text-foreground truncate">{current.titulo}</div>
              <div className="text-[10.5px] text-muted-foreground mt-0.5">
                Termina lo que haces y confírmalo desde la <Bell className="inline w-2.5 h-2.5 -mt-0.5" /> campanita.
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Dialog open modal onOpenChange={() => {}}>
      <DialogContent
        className="max-w-lg p-0 gap-0 overflow-hidden [&>button.absolute]:hidden"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        data-testid="critical-notif-modal"
      >
        {/* Header con progreso */}
        <div className="relative px-6 pt-6 pb-4 bg-gradient-to-b from-rose-50 to-transparent dark:from-rose-500/10">
          <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.15em] text-rose-700 dark:text-rose-400">
            <AlertTriangle className="w-3.5 h-3.5" />
            {total === 1 ? "Aviso importante" : `Avisos importantes · ${idx + 1} de ${total}`}
          </div>
          {total > 1 && (
            <div className="mt-3 h-1 rounded-full bg-muted overflow-hidden">
              <div className="h-full bg-primary transition-all duration-500" style={{ width: `${progressPct}%` }} />
            </div>
          )}
          <p className="text-xs text-muted-foreground mt-3">
            {total > 1
              ? "Necesitamos que revises y confirmes cada uno antes de continuar."
              : "Necesitamos que lo revises y confirmes para continuar."}
          </p>
        </div>

        {/* Contenido */}
        <div className="px-6 py-5">
          <div className="flex items-start gap-3">
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${tone.iconBg}`}>
              <Icon className={`w-5 h-5 ${tone.iconFg}`} />
            </div>
            <div className="min-w-0 flex-1">
              <div className={`inline-block text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider mb-1.5 ${tone.chip}`}>
                {meta.label}
              </div>
              <h2 className="font-display text-xl text-foreground leading-tight" data-testid="critical-notif-title">{current.titulo}</h2>
            </div>
          </div>

          {/* Descripción principal */}
          {current.descripcion && (
            <div className="mt-4 text-[15px] leading-relaxed text-foreground/85 whitespace-pre-line">
              {current.descripcion}
            </div>
          )}

          {/* Meta info agrupada, más discreta */}
          <div className="mt-5 rounded-lg bg-muted/50 border border-border/60 divide-y divide-border/40">
            {current.actor_nombre && (
              <MetaRow icon={UserIcon} label="Enviado por" value={current.actor_nombre} />
            )}
            <MetaRow icon={Clock} label="Cuándo" value={humanTime(current.at || current.created_at)} />
            {current.motivo && (
              <MetaRow icon={Info} label="Motivo" value={current.motivo} />
            )}
            {current.vigencia && (
              <MetaRow icon={Clock} label="Vigencia" value={new Date(current.vigencia).toLocaleDateString("es-CO", { day: "numeric", month: "long", year: "numeric" })} />
            )}
          </div>
        </div>

        {/* Acciones */}
        <div className="px-6 py-4 border-t border-border/60 bg-muted/20 flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="text-[11px] text-muted-foreground">
              {idx + 1 < total ? `Después de este quedan ${total - idx - 1}` : "Este es el último aviso"}
            </div>
            <button
              type="button"
              onClick={cerrarTemp}
              disabled={processing}
              className="text-[11px] font-semibold text-muted-foreground hover:text-foreground underline-offset-2 hover:underline disabled:opacity-50"
              data-testid="critical-postpone"
              title="Seguir trabajando; lo puedes revisar luego desde la campanita"
            >
              Ver después
            </button>
          </div>
          <div className="flex gap-2 flex-wrap">
            {target && (
              <Button
                variant="outline"
                onClick={() => confirm(true)}
                disabled={processing}
                className="h-11 px-4"
                data-testid="critical-view-detail"
              >
                {meta.accion || "Ver detalle"} <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            )}
            <Button
              onClick={() => confirm(false)}
              disabled={processing}
              className="bg-primary hover:bg-primary/90 h-11 px-5"
              data-testid="critical-confirm"
            >
              <CheckCircle2 className="w-4 h-4 mr-2" />
              {idx + 1 < total ? "Confirmar y siguiente" : "Confirmar y cerrar"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function MetaRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3 px-3 py-2">
      <Icon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold min-w-[80px]">{label}</div>
      <div className="text-sm text-foreground/90 flex-1 min-w-0 truncate">{value}</div>
    </div>
  );
}
